import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UsersIcon, ClockIcon, ArrowsClockwiseIcon, CircleIcon } from "@phosphor-icons/react";
import { inr } from "./posHelpers";

const SECTIONS = ["Indoor", "Outdoor", "Bar", "Private", "Rooftop"];

const stateColor = (open) => {
  if (!open) return "bg-[hsl(140_60%_92%)] border-[hsl(140_60%_55%)] text-[hsl(140_60%_25%)]";
  return "bg-[hsl(38_92%_92%)] border-[hsl(38_92%_55%)] text-[hsl(38_92%_25%)]";
};

export function TablesFloor({ activeId, onTableClick, refreshKey }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("all");
  const [openDialogTable, setOpenDialogTable] = useState(null);
  const [guests, setGuests] = useState(1);
  const [custName, setCustName] = useState("");

  const load = () => {
    if (!activeId) return;
    setLoading(true);
    api.get("/tables/status", { params: { property_id: activeId } })
      .then((r) => setTables(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, refreshKey]);

  const filtered = section === "all" ? tables : tables.filter((t) => t.section === section);
  const grouped = SECTIONS.map((s) => ({ section: s, tables: (section === "all" || section === s) ? tables.filter((t) => t.section === s) : [] })).filter((g) => g.tables.length > 0);

  const seatTable = async () => {
    try {
      const r = await api.post("/tables/open", {
        table_id: openDialogTable.id,
        property_id: activeId,
        guest_count: Number(guests) || 1,
        customer_name: custName || null,
      });
      toast.success(`Table ${openDialogTable.number} seated`);
      setOpenDialogTable(null); setGuests(1); setCustName("");
      onTableClick?.(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleClick = (t) => {
    if (t.open_order) {
      onTableClick?.(t.open_order);
    } else {
      setOpenDialogTable(t);
    }
  };

  if (tables.length === 0 && !loading) {
    return (
      <Card className="p-8 text-center">
        <p className="text-sm text-muted-foreground">No tables configured for this property.</p>
        <p className="text-xs text-muted-foreground mt-2">Admin → Admin Console → Tables to configure your floor plan.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSection("all")}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${section === "all" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
            data-testid="tables-floor-filter-all"
          >
            All ({tables.length})
          </button>
          {SECTIONS.map((s) => {
            const count = tables.filter((t) => t.section === s).length;
            if (!count) return null;
            return (
              <button
                key={s}
                onClick={() => setSection(s)}
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${section === s ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                data-testid={`tables-floor-filter-${s}`}
              >
                {s} ({count})
              </button>
            );
          })}
        </div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="tables-floor-refresh"><ArrowsClockwiseIcon size={14} className="mr-1" /> Refresh</Button>
      </div>

      <div className="flex gap-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><CircleIcon size={10} weight="fill" className="text-[hsl(140_60%_45%)]" /> Free</span>
        <span className="flex items-center gap-1"><CircleIcon size={10} weight="fill" className="text-[hsl(38_92%_50%)]" /> Occupied</span>
      </div>

      {grouped.map((g) => (
        <Card key={g.section} className="p-4">
          <div className="flex justify-between items-center mb-3">
            <p className="label-overline">{g.section} · {g.tables.length} tables</p>
            <p className="text-xs text-muted-foreground">{g.tables.filter((t) => t.open_order).length} occupied</p>
          </div>
          <div
            className="relative w-full rounded-lg border border-border/60 bg-muted/10"
            style={{ minHeight: 300 }}
            data-testid={`tables-floor-canvas-${g.section}`}
          >
            {g.tables.map((t) => (
              <button
                key={t.id}
                onClick={() => handleClick(t)}
                className={`absolute select-none rounded-lg border-2 p-2 min-w-[90px] shadow-sm transition-all hover:scale-105 hover:shadow-md ${stateColor(t.open_order)}`}
                style={{ left: `${t.x}%`, top: `${t.y}%` }}
                data-testid={`tables-floor-tile-${t.number}`}
              >
                <p className="font-display text-xl leading-none">{t.number}</p>
                <p className="text-[10px] mt-1 flex items-center justify-center gap-1"><UsersIcon size={10} /> {t.capacity}</p>
                {t.open_order && (
                  <>
                    <p className="text-[10px] font-mono-plex mt-1">{inr(t.open_order.running_total || 0)}</p>
                    <p className="text-[9px] flex items-center justify-center gap-1"><ClockIcon size={9} /> {t.open_order.rounds_count}× round</p>
                  </>
                )}
              </button>
            ))}
          </div>
        </Card>
      ))}

      {/* Seat table dialog */}
      {openDialogTable && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpenDialogTable(null)}>
          <div className="bg-card rounded-lg p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="tables-seat-dialog">
            <h3 className="font-display text-xl mb-3">Seat table {openDialogTable.number}</h3>
            <div className="space-y-3">
              <div>
                <Label>Guest count</Label>
                <Input type="number" min="1" value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="tables-seat-guests" />
              </div>
              <div>
                <Label>Customer name (optional)</Label>
                <Input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Walk-in guest" data-testid="tables-seat-name" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setOpenDialogTable(null)}>Cancel</Button>
              <Button className="flex-1" onClick={seatTable} data-testid="tables-seat-submit">Open bill</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
